import { Box, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API, UTIL*/
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import ProductTypesApi from 'api/setting/default-products/product-type.api'
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import Loading from 'component/Loading'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import SearchPatients from 'component/SearchPatients'
import FilterSelect from 'component/Select/FilterSelect'
import TransactionRevenueList from './TransactionRevenueList'

/**STYLED */
import AboutUs from 'api/admin/clinic-manage/about.api'
import ComponentApi from 'api/components-api'
import ReportPermissionApi from 'api/setting/personnel-managements/report-permission.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import InputCheckbox from 'component/Input/InputCheckbox'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import PageModal from 'new-components/modal/page-modal'
import { dateTimeToView } from 'utils/date.utils'
import TransactionRevenueAll from './transaction-revenue-all'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}
interface DentistInterface {
  dentistId: number
  dentistFullname: string
}
export default function TransactionRevenue() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const componentRefPDF = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange

  const [branchId, setBranchId] = useState<number>(getBranch())
  const [branches, setBranches] = useState<any>([])
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [rowCount, setRowCount] = useState<number>(0)
  const [billStatus, setBillStatus] = useState<string>('ALL')
  const [prefix, setPrefix] = useState<string>('ALL')
  const [receiptPrefix, setReceiptPrefix] = useState<any[]>([])
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')

  const [patientId, setPatientId] = useState<number>(0)

  const [itemCode, setItemCode] = useState<any[]>([])

  const [productTypes, setProductTypes] = useState<any[]>([])
  const [operativeTypes, setOperativeTypes] = useState<any[]>([])
  const [operativeTypeId, setOperativeTypeId] = useState<number>(0)
  const [selectOperativeCodes, setSelectOperativeCodes] = useState<string[]>([])

  const [typeSelected, setTypeSelected] = useState({ productTypeName: '', productTypeId: 0, group: '' })
  const [selectOperativeTypes, setSelectOperativeTypes] = useState<any[]>([])

  const [doctorId, setDoctorId] = useState<number>(0)
  const [doctors, setDoctors] = useState<Doctors[]>([])

  const [transactionData, setTransactionData] = useState<any>([])
  const [transactionDataExport, setTransactionDataExport] = useState<any[]>([])
  const [transactionPage, setTransactionPage] = useState<number>(1)
  const [transactionLimitPage, setTransactionLimitPage] = useState<number>(10)

  const [fetchData, setFetchData] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const [patientFilter, setPatientFilter] = useState<string>(t('REPORT.REPORT_GROUP.ALL'))
  const [selectDoctors, setSelectDoctors] = useState<any[]>(['ALL'])
  const [openFilterDoctors, setOpenFilterDoctors] = useState(false)

  const [isOpenModal, setIsOpenModal] = useState(false)
  const [transactionRevenueList, setTransactionRevenueList] = useState<any>([])
  const [crm, setCrm] = useState(null)
  const [reportId, setReportId] = useState(0)
  const [fetch, setFetch] = useState<boolean>(false)
  const isFetchingRef = useRef(false)
  const isFirstRun = useRef(true)
  const doctorsLoadedRef = useRef(false)


  const fetchAboutUs = async () => {
    try {
      const { data: aboutData } = await AboutUs.findAboutUs()
      const useCrm = aboutData?.useCrm
      setCrm(useCrm)

      const resp: any = await ReportPermissionApi.reportTransactionRevenueList(branchId)
      const permission = resp?.data?.permission ?? []

      if (permission.length === 0) {
        setTransactionRevenueList(defaultTransactionRevenue())
        return
      }

      const hasVoucher = permission.some((item: any) => 'amountVoucher' in item)
      const list =
        useCrm === '1'
          ? hasVoucher
            ? permission
            : permission.flatMap((item: any) =>
                item.amountTreatmentRight !== undefined ? [item, { amountVoucher: '0' }, { amountPromotion: '0' }] : item
              )
          : permission.filter((item: any) => !('amountVoucher' in item) && !('amountPromotion' in item))

      setTransactionRevenueList(list)
    } catch {
      setTransactionRevenueList(defaultTransactionRevenue())
    }
  }

  const setDoc = (data: any) => {
    const sortedSelectData = data.sort((a: any, b: any) => {
      const idA = a.dentistId
      const idB = b.dentistId
      return selectDoctors.indexOf(idA) - selectDoctors.indexOf(idB)
    })
    return sortedSelectData
  }

  // รายการ (รหัส): id = group_productId สำหรับส่ง API (เลือกได้หลายรายการ, แสดงรหัส - ชื่อ)
  // กรองตามประเภทรายการที่เลือก (selectOperativeTypes) เพื่อให้ dropdown สอดคล้องกับ "ประเภทรายการ"
  const operativeCodeOptions = useMemo(() => {
    const filtered = !selectOperativeTypes || selectOperativeTypes.length === 0
      ? itemCode
      : itemCode.filter((item: any) => {
          const prefix = item.group === 'OPERATIVE' ? 'operativeType' : 'productType'
          return selectOperativeTypes.includes(`${prefix}_${item.productTypeId}`)
        })
    return filtered.map((item: any) => {
      const code = item.productCode || item.operativeCode || ''
      const name = item.itemName || item.productName || item.operativeName || code
      const label = code ? `${code} - ${name}` : name
      return { id: `${item.group || 'OPERATIVE'}_${item.productId}`, name: label }
    })
  }, [itemCode, selectOperativeTypes])

  const operativeTypeOptions = useMemo(
    () => [
      ...productTypes.map((item: any) => ({
        id: `productType_${item.productTypeId}`,
        name: item.productTypeName,
        originalData: item,
        type: 'productType'
      })),
      ...operativeTypes.map((item: any) => ({
        id: `operativeType_${item.operativeTypeId}`,
        name: item.operativeTypeName,
        originalData: item,
        type: 'operativeType'
      }))
    ],
    [productTypes, operativeTypes]
  )

  const tableFilter = useMemo(
    () => [
      [
        t('SEARCH_BY'),
        patientFilter,
        t('REPORT.FILTER.BRANCH'),
        _.get(_.find(branches, { branchId }), 'branchName') || _.get(_.find(branches, { branchId }), 'branchNameEn'),
        'วันที่เริ่มต้น',
        rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-',
        'วันที่สิ้นสุด',
        rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-',
        t('REPORT.FILTER.RECEIPT_STATUS'),
        billStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.FILTER.BILL.${billStatus}`)
      ],
      [
        t('REPORT.FILTER.RECEIPT_TYPE'),
        prefix === 'ALL' ? t(`REPORT.FILTER.ALL`) : prefix,
        t('REPORT.FILTER.OPERATIVE_TYPE'),
        _.isEmpty(selectOperativeTypes)
          ? t('REPORT.REPORT_GROUP.ALL')
          : selectOperativeTypes.length === operativeTypeOptions.length
            ? t('REPORT.FILTER.ALL')
            : selectOperativeTypes
                .map((id: string) => operativeTypeOptions.find((opt) => opt.id === id)?.name)
                .filter(Boolean)
                .slice(0, 3)
                .join(', '),
        t('REPORT.FILTER.OPERATIVE_CODE'),
        _.isEmpty(selectOperativeCodes)
          ? t('REPORT.REPORT_GROUP.ALL')
          : (() => {
              const names = selectOperativeCodes.map((id) => operativeCodeOptions.find((o) => o.id === id)?.name).filter(Boolean)
              return names.length > 0 ? names.slice(0, 3).join(', ') : t('REPORT.REPORT_GROUP.ALL')
            })(),
        t('REPORT.FILTER.DOCTOR'),
        _.isEmpty(selectDoctors)
          ? ` ${t('REPORT.FILTER.NO_SELECT')}`
          : selectDoctors.filter((i) => typeof i === 'number').length === doctors.length
            ? `${t('REPORT.FILTER.ALL')}`
            : `${setDoc(doctors.filter((d: any) => selectDoctors.includes(d.userId)))
                ?.map((d: any) => d.fullname)
                .slice(0, 3)
                .join(', ')}`
      ]
    ],
    [
      t,
      patientFilter,
      branches,
      branchId,
      rangeDate,
      billStatus,
      prefix,
      selectOperativeTypes,
      operativeTypeOptions,
      selectOperativeCodes,
      operativeCodeOptions,
      selectDoctors,
      doctors
    ]
  )
  const defaultTransactionRevenue = () => {
    return [
      { datePaid: "0" },
      { billStatus: "0" },
      { billNo: "0" },
      { cnNumber: "0" },
      { branchCnNumber: "0" },
      { snCode: "0" },
      { patientFullname: "0" },
      { doctorFullname: "0" },
      { itemTypeName: "0" },
      { code: "0" },
      { itemName: "0" },
      { price: "0" },
      { qty: "0" },
      { priceTreatment: "0" },
      { dfPercent: "0" },
      { dfOriginal: "0" },
      { cfPercent: "0" },
      { cfOriginal: "0" },
      { dfDiscount: "0" },
      { cfDiscount: "0" },
      { discountTotal: "0" },
      { additionalTotal: "0" },
      { dfNetOriginal: "0" },
      { cfNetOriginal: "0" },
      { total: "0" },
      { PayTreatment: "0" },
      { amountPaidOverdue: "0" },
      { amountPaidAdvance: "0" },
      { amountPaid: "0" },
      { amountCash: "0" },
      { amountTransfer: "0" },
      { amountCredit: "0" },
      { amountDebit: "0" },
      { dfAmountCredit: "0" },
      { cfAmountCredit: "0" },
      { amountTreatmentRight: "0" },
      { amountVoucher: "0" },
      { amountPromotion: "0" },
      { amountAdvance: "0" },
      { dfPaid: "0" },
      { cfPaid: "0" },
      { creditCardFee: "0" },
      { creditCost: "0" },
      { dfNet: "0" },
      { cfNet: "0" },
      { dfOverdue: "0" },
      { cfOverdue: "0" },
      { overdue: "0" },
      { staff: "0" },
      { remark: "0" },
      { cancelRemark: "0" },
      { cancelBy: "0" },
    ]
  }
  const translationMap: Record<string, string> = {
    "datePaid": "วันที่",
    "billStatus": "สถานะใบเสร็จ",
    "billNo": "เลขที่ใบเสร็จ",
    "cnNumber": "รหัสผู้ป่วย",
    "branchCnNumber": "รหัสผู้ป่วยประจำสาขา",
    "snCode": "รหัสผู้ป่วยพิเศษ",
    "patientFullname": "ชื่อผู้ป่วย",
    "doctorFullname": "ทันตแพทย์",
    "itemTypeName": "ประเภทรายการ",
    "code": "รหัส",
    "itemName": "รายการ",
    "price": "ราคาต่อหน่วย",
    "qty": "จำนวน",
    "priceTreatment": "ราคาค่ารักษา",
    "dfPercent": "DF%",
    "dfOriginal": "ยอดค่ารักษา DF",
    "cfPercent": "CF%",
    "cfOriginal": "ยอดค่ารักษา CF",
    "dfDiscount": "ส่วนลดสิทธิการรักษา DF",
    "cfDiscount": "ส่วนลดสิทธิการรักษา CF",
    "discountTotal": "รวมส่วนลด (THB)",
    "additionalTotal": "รวมส่วนเพิ่ม (THB)",
    "dfNetOriginal": "ยอดรวมสุทธิ DF",
    "cfNetOriginal": "ยอดรวมสุทธิ CF",
    "total": "ยอดรวมสุทธิค่ารักษา",
    "PayTreatment": "จ่ายค่ารักษา",
    "amountPaidOverdue": "จ่ายค้างชำระ",
    "amountPaidAdvance": "Advance",
    "amountPaid": "ยอดรับเงินสุทธิ",
    "amountCash": "เงินสด",
    "amountTransfer": "เงินโอน",
    "amountCredit": "เครดิต",
    "amountDebit": "เดบิต",
    "dfAmountCredit": "เครดิต DF",
    "cfAmountCredit": "เครดิต CF",
    "amountTreatmentRight": "สิทธิการรักษา",
    "amountVoucher": "Gift Voucher",
    "amountPromotion": "Promotion",
    "amountAdvance": "Clear Advance",
    "dfPaid": "จ่ายค่ารักษา DF",
    "cfPaid": "จ่ายค่ารักษา CF",
    "creditCardFee": "เครดิต %",
    "creditCost": "ยอดรวมเครดิต %",
    "dfNet": "DF สุทธิ",
    "cfNet": "CF สุทธิ",
    "dfOverdue": "ค้างชำระ DF",
    "cfOverdue": "ค้างชำระ CF",
    "overdue": "Balance",
    "staff": "staff",
    "remark": "Remark",
    "cancelRemark": "Cancel Remark",
    "cancelBy": "Cancel By"
  };

  const verifyPermission = async () => {
    const { data: reportList } = await ReportApi.reportList({ page: 1, pageLimit: 200 })
    const report = _.find(reportList, (item: any) => String(item.reportKey) === 'TRANSACTION_REVENUE')
    if (!report) {
      history.push(routeName.report)
      return false
    }
    setReportId(report.reportId)

    const [branchesRes, prefixRes] = await Promise.allSettled([
      ClinicApi.findAllBranches(),
      ClinicApi.allBillPrefix()
    ])

    if (branchesRes.status === 'fulfilled' && branchesRes.value?.data) setBranches(branchesRes.value.data)
    if (prefixRes.status === 'fulfilled' && prefixRes.value?.data) setReceiptPrefix(prefixRes.value.data)
    // perf: โหลดรายชื่อแพทย์ทำที่ useEffect เดียวด้านล่าง (deps [branchId, rangeDate]) — เลี่ยงโหลดซ้ำ/แข่งกัน setSelectDoctors

    return true
  }

  const loadOperativeType = useCallback((search = '') => {
    OperativeTypeApi.findAll({ page: 1, status: 'ACTIVE', pageLimit: 50, search: search, showNhso: 1 })
      .then((resp) => setOperativeTypes(resp.data))
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [])

  const loadProductType = useCallback((search = '') => {
    ProductTypesApi.findAll({ page: 1, status: 'ACTIVE', pageLimit: 50, search: search, showNhso: 1 })
      .then((resp) => setProductTypes(resp.data))
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [])

  const loadItemCode = useCallback((search = '', itemTypeId = null, group = ``) => {
    ComponentApi.findAllItemCode({ itemTypeId, group, search: search, showNhso: 1 })
      .then((resp) => setItemCode(resp.data))
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [])

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  useEffect(() => {
    loadOperativeType()
    loadProductType()
    loadItemCode()
  }, [])

  useEffect(() => {
    if (rangeDate[1] !== 'Invalid date' && rangeDate[1]) {
      ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1], branchId)
        .then((resp) => {
          setDoctors([{ userId: 999999, fullname: t('REPORT.TITLE.NO_DOCTOR') }, ...resp.data])
          const dentists: DentistInterface[] = resp.data.map((d: any) => {
            return {
              dentistId: d.userId,
              dentistFullname: d.fullname
            }
          })
          const doctorIds = dentists.map((d: DentistInterface) => d.dentistId)
          setSelectDoctors(doctorIds)
          doctorsLoadedRef.current = true
        })
        .catch((e) => {
          return
        })
    }
    // perf: doctor loader เดียวของหน้านี้ — รีโหลดเมื่อสาขา/ช่วงวันเปลี่ยน (เดิม verifyPermission โหลดซ้ำอีกที)
  }, [branchId, rangeDate[0], rangeDate[1]])


  const fetchTransaction = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);

    let condition: FindAllReportInterface = {
      page: transactionPage,
      pageLimit: transactionLimitPage,
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      operativeType: operativeTypeId,
      groupCode: '',
      group: '',
      patientId: patientId,
      billStatus: billStatus
    }
    if (selectOperativeCodes && selectOperativeCodes.length > 0) {
      const firstId = selectOperativeCodes[0]
      const firstParts = firstId.split('_')
      const firstGroup = firstParts[0]
      const codeGroup = firstGroup === 'OPERATIVE' ? '' : firstGroup
      const sameGroupIds = selectOperativeCodes
        .filter((id) => id.startsWith(`${firstGroup}_`))
        .map((id) => id.replace(`${firstGroup}_`, ''))
        .filter((id) => id && id !== '0')
      if (sameGroupIds.length > 0) {
        condition.operativeCode = sameGroupIds.join(',')
        condition.groupCode = codeGroup
        condition.group = codeGroup
      }
    }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (prefix) condition = { ...condition, prefix: prefix === 'ALL' ? '' : prefix }
    if (rangeDate) {
      if (rangeDate[0] !== null && rangeDate[1] !== null) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    }
    if (selectDoctors && selectDoctors.length > 0) {
      const isAllSelected = selectDoctors.includes('ALL')
      const filteredDoctors = isAllSelected ? selectDoctors.filter((doctor) => doctor !== 'ALL') : selectDoctors
      const doctorIds = filteredDoctors.filter((doctor) => typeof doctor === 'number' && doctor > 0)
      if (doctorIds.length > 0) {
        condition = { ...condition, doctorId: doctorIds.join(',').replace(/^,|,$/g, '') }
      }
    }
    if (selectOperativeTypes && selectOperativeTypes.length > 0) {
      const productTypeIds = selectOperativeTypes
        .filter((id: string) => id.startsWith('productType_'))
        .map((id: string) => parseInt(id.replace('productType_', ''), 10))
        .filter((id: number) => !Number.isNaN(id) && id > 0)
      const operativeTypeIds = selectOperativeTypes
        .filter((id: string) => id.startsWith('operativeType_'))
        .map((id: string) => parseInt(id.replace('operativeType_', ''), 10))
        .filter((id: number) => !Number.isNaN(id) && id > 0)
      if (operativeTypeIds.length > 0) {
        condition = { ...condition, operativeType: operativeTypeIds.join(',') }
      }
      if (productTypeIds.length > 0) {
        condition = { ...condition, productType: productTypeIds.join(',') }
      }
    }
    try {
      const response = await ReportApi.reportTransactionRevenue(condition)
      setTransactionData(response.data)
      setRowCount(response.headers['x-total'])
    } catch (e) {
      // Handle error if needed
    } finally {
      setLoading(false)
      isFetchingRef.current = false
      setFetchData(false)
    }
  }, [branchId, transactionPage, transactionLimitPage, prefix, rangeDate, patientId, billStatus, operativeTypeId, selectDoctors, sortBy, sortType, doctors, selectOperativeTypes, selectOperativeCodes])

  const fetchTransactionExport = useCallback(async () => {
    let condition: FindAllReportInterface = {
      page: transactionPage,
      pageLimit: transactionLimitPage,
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      operativeType: operativeTypeId,
      groupCode: '',
      group: '',
      patientId: patientId,
      billStatus: billStatus,
      export: 1
    }
    if (selectOperativeCodes && selectOperativeCodes.length > 0) {
      const firstId = selectOperativeCodes[0]
      const firstParts = firstId.split('_')
      const firstGroup = firstParts[0]
      const codeGroup = firstGroup === 'OPERATIVE' ? '' : firstGroup
      const sameGroupIds = selectOperativeCodes
        .filter((id) => id.startsWith(`${firstGroup}_`))
        .map((id) => id.replace(`${firstGroup}_`, ''))
        .filter((id) => id && id !== '0')
      if (sameGroupIds.length > 0) {
        condition.operativeCode = sameGroupIds.join(',')
        condition.groupCode = codeGroup
        condition.group = codeGroup
      }
    }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (prefix) condition = { ...condition, prefix: prefix === 'ALL' ? '' : prefix }
    if (rangeDate) {
      if (rangeDate[0] !== null && rangeDate[1] !== null) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    }

    if (selectDoctors && selectDoctors.length > 0) {
      const isAllSelected = selectDoctors.includes('ALL')
      const filteredDoctors = isAllSelected ? selectDoctors.filter((doctor) => doctor !== 'ALL') : selectDoctors
      const doctorIds = filteredDoctors.filter((doctor) => typeof doctor === 'number' && doctor > 0)
      if (doctorIds.length > 0) {
        condition = { ...condition, doctorId: doctorIds.join(',').replace(/^,|,$/g, '') }
      }
    }
    if (selectOperativeTypes && selectOperativeTypes.length > 0) {
      const productTypeIds = selectOperativeTypes
        .filter((id: string) => id.startsWith('productType_'))
        .map((id: string) => parseInt(id.replace('productType_', ''), 10))
        .filter((id: number) => !Number.isNaN(id) && id > 0)
      const operativeTypeIds = selectOperativeTypes
        .filter((id: string) => id.startsWith('operativeType_'))
        .map((id: string) => parseInt(id.replace('operativeType_', ''), 10))
        .filter((id: number) => !Number.isNaN(id) && id > 0)
      if (operativeTypeIds.length > 0) {
        condition = { ...condition, operativeType: operativeTypeIds.join(',') }
      }
      if (productTypeIds.length > 0) {
        condition = { ...condition, productType: productTypeIds.join(',') }
      }
    }

    try {
      const response = await ReportApi.reportTransactionRevenue(condition)
      setTransactionDataExport(response.data)
    } catch (e) {
      // Handle error if needed
    } finally {
      setLoading(false)
    }
  }, [branchId, transactionPage, transactionLimitPage, prefix, rangeDate, patientId, billStatus, operativeTypeId, selectDoctors, sortBy, sortType, doctors, selectOperativeTypes, selectOperativeCodes])
  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.TRANSACTION_REVENUE`)} ${rangeDate[0] ? `(${rangeDate[0]}-${rangeDate[1]})` : ''}  `,
    sheetName: `${t(`REPORT.REPORT_NAME.TRANSACTION_REVENUE`)} `
  })

  const handleClear = useCallback(() => setPatientId(0), [])

  const onRequestSort = useCallback((sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }, [])

  const handleChangeRowsPerPage = useCallback((limit: number) => setTransactionLimitPage(limit), [])

  const handleChangePage = useCallback((val: number) => setTransactionPage(val), [])

  const onOperativeTypeChange = useCallback((ids: any) => {
    setSelectOperativeTypes(ids)
    setSelectOperativeCodes([])
  }, [])

  const onOperativeCodeChange = useCallback((ids: any) => {
    setSelectOperativeCodes(ids)
  }, [])

  const onDoctorsChange = useCallback((ids: any) => {
    setSelectDoctors(ids)
    setOpenFilterDoctors(false)
  }, [])

  const doctorOptions = useMemo(
    () => doctors.map((d: any) => ({ id: d.userId, name: d.fullname })),
    [doctors]
  )

  // useEffect สำหรับ fetch เมื่อกดปุ่มค้นหา
  // ดึง exportData (ทุกแถว) มาด้วย เพื่อให้ section สรุปด้านล่าง (allSummary/allSummaryOverDue) มีข้อมูลคำนวณ
  useEffect(() => {
    if (fetchData && selectDoctors && selectDoctors.length > 0 && doctorsLoadedRef.current) {
      fetchTransaction()
      fetchTransactionExport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, selectDoctors])

  // useEffect สำหรับเรียก API ครั้งแรกเมื่อ doctors โหลดเสร็จ
  useEffect(() => {
    if (isFirstRun.current && doctorsLoadedRef.current && selectDoctors && selectDoctors.length > 0) {
      isFirstRun.current = false
      fetchTransaction()
      fetchTransactionExport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDoctors])

  useEffect(() => {
    if (!isFirstRun.current && !fetchData && !isFetchingRef.current) {
      fetchTransaction()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionPage, transactionLimitPage, sortBy, sortType])

  useEffect(() => {
    fetchAboutUs()
  }, [])

  const MAX_SELECTION = crm === '1' ? 25 : 23

  const calculateAdvance = useMemo(
    () => _.get(_.find(branches, { branchId }), 'calculateAdvance') === 'DECREASE',
    [branches, branchId]
  )

  const handleCheckboxChange = useCallback((key: string) => {
    setTransactionRevenueList((prevList: any) => {
      const selectedCount = prevList.filter((item: any) => Object.values(item)[0] === '1').length
      return prevList.map((item: any) => {
        if (Object.keys(item)[0] === key) {
          const newValue = item[key] === '1' ? '0' : '1'
          if (selectedCount >= MAX_SELECTION && newValue === '1') return item
          return { [key]: newValue }
        }
        return item
      })
    })
  }, [MAX_SELECTION])



  const filteredData = useMemo(
    () => transactionRevenueList.filter((item: any) => Object.values(item)[0] === '1'),
    [transactionRevenueList]
  )
  const selectedColumns = useMemo(
    () => filteredData.map((item: any) => Object.keys(item)[0]),
    [filteredData]
  )


  const renderTable = useCallback((data: any, index: number) => {
    const result = []
    if (transactionRevenueList[0]?.datePaid === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{dateTimeToView(data.datePaid)}</TableCell>)
    if (transactionRevenueList[1]?.billStatus === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{t(`REPORT.BILL_STATUS.${data.billStatus}`)}</TableCell>)
    if (transactionRevenueList[2]?.billNo === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.billNo} {(data?.isNhso === '1' ? <span style={{ color: '#2F4169', fontSize: 12, fontWeight: "bold" }}>{` (สปสช.) `}</span> : '')} </TableCell>)
    if (transactionRevenueList[3]?.cnNumber === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.cnNumber}</TableCell>)
    if (transactionRevenueList[4]?.branchCnNumber === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.branchCnNumber}</TableCell>)
    if (transactionRevenueList[5]?.snCode === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.snCode}</TableCell>)
    if (transactionRevenueList[6]?.patientFullname === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.patientFullname}</TableCell>)
    if (transactionRevenueList[7]?.doctorFullname === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.doctorFullname}</TableCell>)
    if (transactionRevenueList[8]?.itemTypeName === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.itemTypeName}</TableCell>)
    if (transactionRevenueList[9]?.code === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.code}</TableCell>)
    if (transactionRevenueList[10]?.itemName === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data.itemName}</TableCell>)
    if (transactionRevenueList[11]?.price === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400, }}>{numberFormat(data?.price)}</TableCell>)
    if (transactionRevenueList[12]?.qty === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(1)}</TableCell>)
    if (transactionRevenueList[13]?.priceTreatment === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.price * data?.qty)}</TableCell>)
    if (transactionRevenueList[14]?.dfPercent === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.dfUnit === 'PERCENT' ? data.dfPercentOriginal + '%' : data.dfBahtOriginal + 'บาท' || '-'}</TableCell>)
    if (transactionRevenueList[15]?.dfOriginal === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfOriginal)}</TableCell>)
    if (transactionRevenueList[16]?.cfPercent === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.dfUnit === 'PERCENT' ? data.cfPercentOriginal + '%' : data.cfBahtOriginal + 'บาท' || '-'}</TableCell>)
    if (transactionRevenueList[17]?.cfOriginal === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfOriginal)}</TableCell>)
    if (transactionRevenueList[18]?.dfDiscount === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfDiscount)}</TableCell>)
    if (transactionRevenueList[19]?.cfDiscount === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfDiscount)}</TableCell>)
    if (transactionRevenueList[20]?.discountTotal === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.discountTotal)}</TableCell>)
    if (transactionRevenueList[21]?.additionalTotal === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.additionalTotal)}</TableCell>)
    if (transactionRevenueList[22]?.dfNetOriginal === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfNetOriginal)}</TableCell>)
    if (transactionRevenueList[23]?.cfNetOriginal === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfNetOriginal)}</TableCell>)
    if (transactionRevenueList[24]?.total === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.total)}</TableCell>)
    if (transactionRevenueList[25]?.PayTreatment === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountPaid)}</TableCell>)
    if (transactionRevenueList[26]?.amountPaidOverdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountPaidOverdue)}</TableCell>)
    if (transactionRevenueList[27]?.amountPaidAdvance === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountPaidAdvance)}</TableCell>)
    if (transactionRevenueList[28]?.amountPaid === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountPaid)}</TableCell>)
    if (transactionRevenueList[29]?.amountCash === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountCash)}</TableCell>)
    if (transactionRevenueList[30]?.amountTransfer === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountTransfer)}</TableCell>)
    if (transactionRevenueList[31]?.amountCredit === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountCredit)}</TableCell>)
    if (transactionRevenueList[32]?.amountDebit === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountDebit)}</TableCell>)
    if (transactionRevenueList[33]?.dfAmountCredit === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfAmountCredit)}</TableCell>)
    if (transactionRevenueList[34]?.cfAmountCredit === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfAmountCredit)}</TableCell>)
    if (transactionRevenueList[35]?.amountTreatmentRight === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountTreatmentRight)}</TableCell>)
    if (crm === '1') {
      if (transactionRevenueList[36]?.amountVoucher === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountVoucher)}</TableCell>)
      if (transactionRevenueList[37]?.amountPromotion === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountPromotion)}</TableCell>)
      if (transactionRevenueList[38]?.amountAdvance === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountAdvance)}</TableCell>)
      if (transactionRevenueList[39]?.dfPaid === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfPaid)}</TableCell>)
      if (transactionRevenueList[40]?.cfPaid === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfPaid)}</TableCell>)
      if (transactionRevenueList[41]?.creditCardFee === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data.creditCardFee, 3, 3) || '-'}</TableCell>)
      if (transactionRevenueList[42]?.creditCost === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data.creditCost, 3, 3) || '-'}</TableCell>)
      if (transactionRevenueList[43]?.dfNet === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfNet)}</TableCell>)
      if (transactionRevenueList[44]?.cfNet === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfNet)}</TableCell>)
      if (transactionRevenueList[45]?.dfOverdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfOverdue)}</TableCell>)
      if (transactionRevenueList[46]?.cfOverdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfOverdue)}</TableCell>)
      if (transactionRevenueList[47]?.overdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.overdue)}</TableCell>)
      if (transactionRevenueList[48]?.staff === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.staff}</TableCell>)
      if (transactionRevenueList[49]?.remark === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.remark || '-'}</TableCell>)
      if (transactionRevenueList[50]?.cancelRemark === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.cancelRemark || '-'}</TableCell>)
      if (transactionRevenueList[51]?.cancelBy === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.cancelBy || '-'}</TableCell>)
    } else {
      if (transactionRevenueList[36]?.amountAdvance === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.amountAdvance)}</TableCell>)
      if (transactionRevenueList[37]?.dfPaid === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfPaid)}</TableCell>)
      if (transactionRevenueList[38]?.cfPaid === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfPaid)}</TableCell>)
      if (transactionRevenueList[39]?.creditCardFee === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data.creditCardFee, 3, 3) || '-'}</TableCell>)
      if (transactionRevenueList[40]?.creditCost === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data.creditCost, 3, 3) || '-'}</TableCell>)
      if (transactionRevenueList[41]?.dfNet === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfNet)}</TableCell>)
      if (transactionRevenueList[42]?.cfNet === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfNet)}</TableCell>)
      if (transactionRevenueList[43]?.dfOverdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.dfOverdue)}</TableCell>)
      if (transactionRevenueList[44]?.cfOverdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.cfOverdue)}</TableCell>)
      if (transactionRevenueList[45]?.overdue === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{numberFormat(data?.overdue)}</TableCell>)
      if (transactionRevenueList[46]?.staff === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.staff}</TableCell>)
      if (transactionRevenueList[47]?.remark === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.remark || '-'}</TableCell>)
      if (transactionRevenueList[48]?.cancelRemark === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.cancelRemark || '-'}</TableCell>)
      if (transactionRevenueList[49]?.cancelBy === '1') result.push(<TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.cancelBy || '-'}</TableCell>)
    }
    return result.length > 0 ? result : null
  }, [transactionRevenueList, crm, t])

  const stylePDF = (index: number) => {
    switch (translationMap[selectedColumns[index]]) {
      case 'วันที่':
        return { minWidth: "39px", maxWidth: "39px" }
      case 'สถานะใบเสร็จ':
        return { minWidth: "39px", maxWidth: "39px" }
      case 'เลขที่ใบเสร็จ':
        return { minWidth: "40px", maxWidth: "40px" }
      // case 'รหัสผู้ป่วย':
      //   return { width: "52px" }
      //   case 'รหัสผู้ป่วยประจำสาขา':
      // return { width: "52px" }
      // case 'รหัสผู้ป่วยพิเศษ':
      // return { width: "52px" }
      case 'ชื่อผู้ป่วย':
        return { minWidth: "47px" }
      case 'ทันตแพทย์':
        return { minWidth: "47px" }
      case 'ประเภทรายการ':
        return { minWidth: "55px", }
      case 'รหัส':
        return { minWidth: "29px", maxWidth: "29px" }
      case 'รายการ':
        return { minWidth: "55px" }
      case 'ราคาต่อหน่วย':
        return { minWidth: "38px", maxWidth: "38px" }
      case 'จำนวน':
        return { minWidth: "23px", maxWidth: "23px" }
      case 'ราคาค่ารักษา':
        return { minWidth: "38px", maxWidth: "38px" }
      // case 'DF%':
      //   return { width: "16px" }
      // case 'ยอดค่ารักษา DF':
      //   return { width: "16px" }
      // case 'CF%':
      //   return { width: "16px" }
      // case 'ยอดค่ารักษา CF':
      //   return { width: "16px" }
      // case 'ส่วนลดสิทธิการรักษา DF':
      //   return { width: "33px" }
      // case 'ส่วนลดสิทธิการรักษา CF':
      //   return { width: "33px" }
      case 'รวมส่วนลด (THB)':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'รวมส่วนเพิ่ม (THB)':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ยอดรวมสุทธิ DF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ยอดรวมสุทธิ CF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ยอดรวมสุทธิค่ารักษา':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'จ่ายค่ารักษา':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'จ่ายค้างชำระ':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'Advance':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ยอดรับเงินสุทธิ':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เงินสด':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เงินโอน':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เครดิต':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เดบิต':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เครดิต DF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เครดิต CF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'สิทธิการรักษา':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'Gift Voucher':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'Promotion':
        return { minWidth: '37px', maxWidth: '37px', letterSpacing: '0px' }
      case 'Clear Advance':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'จ่ายค่ารักษา DF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'จ่ายค่ารักษา CF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'เครดิต %"':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ยอดรวมเครดิต %':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'DF สุทธิ':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'CF สุทธิ':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ค้างชำระ DF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'ค้างชำระ CF':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'Balance':
        return { minWidth: '35px', maxWidth: '35px', letterSpacing: '0px' }
      case 'staff':
        return { minWidth: '35px', maxWidth: '100px', letterSpacing: '0px' }
      case 'Remark':
        return { minWidth: '35px', maxWidth: '100px', letterSpacing: '0px' }
      case 'Cancel Remark':
        return { minWidth: '35px', maxWidth: '100px', letterSpacing: '0px' }
      case 'Cancel By':
        return { minWidth: '35px', maxWidth: '100px', letterSpacing: '0px' }
      default:
        break;
    }
  }

  const onSubmitPDF = async () => {
    await setIsOpenModal(false)
    await ReportPermissionApi.PatchReportTransactionRevenueList(branchId, { reportId: reportId, permission: transactionRevenueList })
    const style = document.createElement("style");
    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  }

  const renderTablePDF = (data: any) => {
    const result = []
    if (transactionRevenueList[0]?.datePaid === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', maxWidth: '39px', fontWeight: 400, textAlign: 'center', width: '125px', border: '1px solid black' }}>{dateTimeToView(data.datePaid)}</td>)
    if (transactionRevenueList[1]?.billStatus === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', maxWidth: '39px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black' }}>{t(`REPORT.BILL_STATUS.${data.billStatus}`)}</td>)
    if (transactionRevenueList[2]?.billNo === '1') result.push(<td style={{ fontSize: '8.5px', maxWidth: '39px', letterSpacing: '0px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black' }}>{data.billNo} {(data?.isNhso === '1' ? <span style={{ color: '#2F4169', fontSize: 7 }}>{` (สปสช.) `}</span> : '')}</td>)
    if (transactionRevenueList[3]?.cnNumber === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', fontWeight: 400, textAlign: 'center', width: '125px', border: '1px solid black' }}>{data.cnNumber}</td>)
    if (transactionRevenueList[4]?.branchCnNumber === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', fontWeight: 400, textAlign: 'center', width: '125px', border: '1px solid black' }}>{data.branchCnNumber}</td>)
    if (transactionRevenueList[5]?.snCode === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', fontWeight: 400, textAlign: 'center', width: '125px', border: '1px solid black' }}>{data.snCode || '-'}</td>)
    if (transactionRevenueList[6]?.patientFullname === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', maxWidth: '47px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black' }}>{data.patientFullname}</td>)
    if (transactionRevenueList[7]?.doctorFullname === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', maxWidth: '47px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black' }}>{data.doctorFullname}</td>)
    if (transactionRevenueList[8]?.itemTypeName === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', maxWidth: '57px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black' }}>{data.itemTypeName}</td>)
    if (transactionRevenueList[9]?.code === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', maxWidth: '29px', fontWeight: 400, textAlign: 'center', width: '125px', border: '1px solid black' }}>{data.code}</td>)
    if (transactionRevenueList[10]?.itemName === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '0px', maxWidth: '57px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black' }}>{data.itemName}</td>)
    if (transactionRevenueList[11]?.price === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', maxWidth: '38px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.price)}</td>)
    if (transactionRevenueList[12]?.qty === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', maxWidth: '23px', fontWeight: 400, textAlign: 'center', width: '125px', border: '1px solid black' }}>{data?.qty}</td>)
    if (transactionRevenueList[13]?.priceTreatment === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', maxWidth: '38px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.price * data?.qty)}</td>)
    if (transactionRevenueList[14]?.dfPercent === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{data?.dfUnit === 'PERCENT' ? data.dfPercentOriginal + '%' : data.dfBahtOriginal + 'บาท' || '-'}</td>)
    if (transactionRevenueList[15]?.dfOriginal === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.dfOriginal)}</td>)
    if (transactionRevenueList[16]?.cfPercent === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{data?.dfUnit === 'PERCENT' ? data.cfPercentOriginal + '%' : data.cfBahtOriginal + 'บาท' || '-'}</td>)
    if (transactionRevenueList[17]?.cfOriginal === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfOriginal || 0)}</td>)
    if (transactionRevenueList[18]?.dfDiscount === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfDiscount)}</td>)
    if (transactionRevenueList[19]?.cfDiscount === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfDiscount)}</td>)
    if (transactionRevenueList[20]?.discountTotal === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.discountTotal)}</td>)
    if (transactionRevenueList[21]?.additionalTotal === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.additionalTotal)}</td>)
    if (transactionRevenueList[22]?.dfNetOriginal === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfNetOriginal)}</td>)
    if (transactionRevenueList[23]?.cfNetOriginal === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfNetOriginal)}</td>)
    if (transactionRevenueList[24]?.total === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.total)}</td>)
    if (transactionRevenueList[25]?.PayTreatment === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountPaid)}</td>)
    if (transactionRevenueList[26]?.amountPaidOverdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountPaidOverdue)}</td>)
    if (transactionRevenueList[27]?.amountPaidAdvance === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountPaidAdvance)}</td>)
    if (transactionRevenueList[28]?.amountPaid === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountPaid)}</td>)
    if (transactionRevenueList[29]?.amountCash === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountCash)}</td>)
    if (transactionRevenueList[30]?.amountTransfer === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountTransfer)}</td>)
    if (transactionRevenueList[31]?.amountCredit === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountCredit)}</td>)
    if (transactionRevenueList[32]?.amountDebit === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountDebit)}</td>)
    if (transactionRevenueList[33]?.dfAmountCredit === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfAmountCredit)}</td>)
    if (transactionRevenueList[34]?.cfAmountCredit === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfAmountCredit)}</td>)
    if (transactionRevenueList[35]?.amountTreatmentRight === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountTreatmentRight)}</td>)
    if (crm === '1') {
      if (transactionRevenueList[36]?.amountVoucher === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountVoucher)}</td>)
      if (transactionRevenueList[37]?.amountPromotion === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '37px' }}>{numberFormat(data?.amountPromotion)}</td>)
      if (transactionRevenueList[38]?.amountAdvance === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountAdvance)}</td>)
      if (transactionRevenueList[39]?.dfPaid === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfPaid)}</td>)
      if (transactionRevenueList[40]?.cfPaid === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfPaid)}</td>)
      if (transactionRevenueList[41]?.creditCardFee === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data.creditCardFee, 3, 3) || '-'}</td>)
      if (transactionRevenueList[42]?.creditCost === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data.creditCost, 3, 3) || '-'}</td>)
      if (transactionRevenueList[43]?.dfNet === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfNet)}</td>)
      if (transactionRevenueList[44]?.cfNet === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfNet)}</td>)
      if (transactionRevenueList[45]?.dfOverdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfOverdue)}</td>)
      if (transactionRevenueList[46]?.cfOverdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfOverdue)}</td>)
      if (transactionRevenueList[47]?.overdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.overdue)}</td>)
      if (transactionRevenueList[48]?.staff === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.staff}</td>)
      if (transactionRevenueList[49]?.remark === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.remark || '-'}</td>)
      if (transactionRevenueList[50]?.cancelRemark === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.cancelRemark || '-'}</td>)
      if (transactionRevenueList[51]?.cancelBy === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.cancelBy || '-'}</td>)
    } else {
      if (transactionRevenueList[36]?.amountAdvance === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.amountAdvance)}</td>)
      if (transactionRevenueList[37]?.dfPaid === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.dfPaid)}</td>)
      if (transactionRevenueList[38]?.cfPaid === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data?.cfPaid)}</td>)
      if (transactionRevenueList[39]?.creditCardFee === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black', maxWidth: '35px' }}>{numberFormat(data.creditCardFee, 3, 3) || '-'}</td>)
      if (transactionRevenueList[40]?.creditCost === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data.creditCost, 3, 3) || '-'}</td>)
      if (transactionRevenueList[41]?.dfNet === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.dfNet)}</td>)
      if (transactionRevenueList[42]?.cfNet === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.cfNet)}</td>)
      if (transactionRevenueList[43]?.dfOverdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.dfOverdue)}</td>)
      if (transactionRevenueList[44]?.cfOverdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.cfOverdue)}</td>)
      if (transactionRevenueList[45]?.overdue === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'right', width: '125px', border: '1px solid black' }}>{numberFormat(data?.overdue)}</td>)
      if (transactionRevenueList[46]?.staff === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.staff}</td>)
      if (transactionRevenueList[47]?.remark === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.remark || '-'}</td>)
      if (transactionRevenueList[48]?.cancelRemark === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.cancelRemark || '-'}</td>)
      if (transactionRevenueList[49]?.cancelBy === '1') result.push(<td style={{ fontSize: '8.5px', letterSpacing: '-0.6px', fontWeight: 400, textAlign: 'left', width: '125px', border: '1px solid black', maxWidth: '100px' }}>{data?.cancelBy || '-'}</td>)
    }
    return result.length > 0 ? result : null
  }
  return (
    <Box>
      <PageModal
        isOpen={isOpenModal}
        overflow
        size='M'
        title={`ส่งออกตารางเป็น PDF`}
        handleClose={() => setIsOpenModal(false)}
        onCancel={{ onClick: () => setIsOpenModal(false) }}
        onSubmit={{ onClick: () => onSubmitPDF(), title: 'พิมพ์' }}
      >
        <Box>
          <FilterSelect
            formControlClass={'filter filter-headCells'}
            formControlStyle={{ maxWidth: 'unset !important' }}
            label={t('REPORT.FILTER.COLUMN_EXPORT')}
            selectId="select-headCells"
            labelId="label-headCells"
            fullWidth
            multiple
            heightDropdown={400}
            value={transactionRevenueList}
            renderValue={() => {
              return transactionRevenueList
                .filter((item: any) => {
                  const value = Object.values(item)[0];
                  return value === "1";
                })
                .map((item: any) => translationMap[Object.keys(item)[0]])
                .join(", ")
            }}
            options={[
              <div key="box">

                <Typography fontSize="14px" fontWeight={400} color="red" marginLeft="24px" marginTop="16px">
                  {`หมายเหตุ : สามารถเลือกได้สูงสุด ${MAX_SELECTION} รายการ (${transactionRevenueList.filter((item: any) => Object.values(item)[0] === "1").length}/${MAX_SELECTION})`}
                </Typography>
                <div key="group-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  {_.map(transactionRevenueList, (item, index) => {
                    const key = Object.keys(item)[0];
                    const isChecked = item[key] === "1";
                    const selectedCount = transactionRevenueList.filter((item: any) => Object.values(item)[0] === "1").length;
                    const isDisabled = selectedCount >= MAX_SELECTION && !isChecked; // ถ้าครบ 23 อันแล้ว ห้ามติ๊กเพิ่ม
                    return (
                      <MenuItem key={index} value={key} style={{ display: "flex", alignItems: "center" }} disabled={isDisabled} onClick={() => handleCheckboxChange(key)}>
                        <InputCheckbox
                          value={key}
                          checked={isChecked}
                          disabled={isDisabled}
                        />
                        {translationMap[key]}
                      </MenuItem>
                    )
                  })}
                </div>
              </div>
            ]}
          />
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden', marginTop: '16px' }}>
            <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
              <Table aria-label="simple table" sx={{ width: '100%' }}>
                <TableHead sx={{ backgroundColor: "#2F4269", color: 'white !important', border: '1px solid #2F4269' }}>
                  <TableRow>
                    {_.map(filteredData, (_item, index) => (
                      <TableCell sx={{ color: "white !important", fontWeight: 500, minWidth: '200px' }}>{translationMap[selectedColumns[index]]}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {_.map(transactionData?.rows, (item, index: number) =>
                    <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                      {renderTable(item, index)}
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* <TransactionRevenuePDF filterData={filteredData} transactionData={transactionData} translationMap={translationMap} selectedColumns={selectedColumns} /> */}
        </Box>
      </PageModal>
      <HeaderCard text={t(`REPORT.REPORT_NAME.TRANSACTION_REVENUE`)} />
      <Box padding="16px">
        <Grid container spacing="8px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SearchPatients
              status="ACTIVE"
              callbackPatientId={setPatientId}
              patientId={patientId}
              onClear={handleClear}
              callbackPatientName={(value) => {
                setPatientFilter(value)
              }}
              bodyHeight={40}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => {
                setBranchId(Number(e.target.value));
              }}
              options={[
                ...branches.map((b: any) => ({
                  value: b.branchId,
                  name: b.branchName || b.branchNameEn || ''
                }))]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              label={''}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.RECEIPT_STATUS')}
              value={billStatus}
              onChange={e => {
                setBillStatus(e.target.value)
              }}
              options={[
                { value: 'ALL', name: t('REPORT.FILTER.ALL') },
                { value: 'PAID', name: t('REPORT.FILTER.BILL.PAID') },
                { value: 'CANCEL', name: t('REPORT.FILTER.BILL.CANCEL') }

              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.RECEIPT_TYPE')}
              value={prefix}
              onChange={e => {
                setPrefix(e.target.value)
              }}
              options={[
                { value: "ALL", name: t('REPORT.FILTER.ALL') },
                ...receiptPrefix.map((b: any) => ({
                  value: b.prefix,
                  name: b.prefix
                }))]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('REPORT.FILTER.OPERATIVE_TYPE')}
              options={operativeTypeOptions}
              selected={selectOperativeTypes}
              onChange={onOperativeTypeChange}
              fullWidth
              width="100%"
              searchable
              searchPlaceholder={t('INPUT.SEARCH') || 'ค้นหา'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('REPORT.FILTER.OPERATIVE_CODE')}
              options={operativeCodeOptions}
              selected={selectOperativeCodes}
              onChange={onOperativeCodeChange}
              fullWidth
              width="100%"
              searchable
              searchPlaceholder={t('INPUT.SEARCH') || 'ค้นหา'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('USER_DOCTOR')}
              options={doctorOptions}
              selected={selectDoctors}
              onChange={onDoctorsChange}
              fullWidth
              width="100%"
              searchable
              searchPlaceholder={t('INPUT.SEARCH') || 'ค้นหา'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1} lg={1}>
            <MainButton
              onClick={() => {
                setFetch(true)
                setFetchData(true)
                setTransactionPage(1)
                setLoading(true)
              }}
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1} lg={5} sx={{ display: 'flex', justifyContent: 'flex-end', ml: 'auto' }}>
            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
              if (e === 'Excel') onDownload()
              if (e === 'PDF') {
                setIsOpenModal(true)
              }
            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
          </Grid>
        </Grid>
        <Box paddingTop={'16px'}>
          <TransactionRevenueList
            tableFilter={tableFilter}
            page={transactionPage}
            pageLimit={transactionLimitPage}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={transactionData}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            componentRef={componentRef}
            exportData={transactionDataExport}
            billStatus={billStatus}
            calculateAdvance={calculateAdvance}
          />
        </Box>
        <Box
          id="print-table-container"
          ref={componentRefPDF}
        >
          <Typography fontSize="10px" fontWeight='bold'>{t('REPORT.REPORT_NAME.TRANSACTION_REVENUE')}</Typography>
          <Box display="flex" flexDirection="row" alignItems="center" marginY="8px">
            <Typography fontSize="10px" fontWeight={600} marginRight="4px">{tableFilter?.[0]?.[2]}:</Typography>
            <Typography fontSize="10px" fontWeight={400} marginRight="16px">{tableFilter?.[0]?.[3]}</Typography>
            <Typography fontSize="10px" fontWeight={600} marginRight="4px">{tableFilter?.[1]?.[0]}:</Typography>
            <Typography fontSize="10px" fontWeight={400} marginRight="16px">{tableFilter?.[1]?.[1]}</Typography>
            <Typography fontSize="10px" fontWeight={600} marginRight="4px">{tableFilter?.[1]?.[2]}:</Typography>
            <Typography fontSize="10px" fontWeight={400} marginRight="16px">{tableFilter?.[1]?.[3]}</Typography>
            <Typography fontSize="10px" fontWeight={600} marginRight="4px">{tableFilter?.[0]?.[8]}:</Typography>
            <Typography fontSize="10px" fontWeight={400} marginRight="16px">{tableFilter?.[0]?.[9]}</Typography>
            <Typography fontSize="10px" fontWeight={600} marginRight="4px">{tableFilter?.[0]?.[4]}-{tableFilter?.[0]?.[6]}:</Typography>
            <Typography fontSize="10px" fontWeight={400} marginRight="16px">{tableFilter?.[0]?.[5]} - {tableFilter?.[0]?.[7]}</Typography>
          </Box>
          <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'center', fontSize: '8.5px', fontWeight: 800, backgroundColor: '#C1C4C5' }}>
                {_.map(filteredData, (_item, index: number) => (
                  <th style={{ ...stylePDF(index), border: '1px solid black', letterSpacing: '0px' }}>{translationMap[selectedColumns[index]]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {_.map(transactionData?.rows, (item, index: number) =>
                <tr key={index}>
                  {renderTablePDF(item)}
                </tr>)}
            </tbody>
          </table>
          <TransactionRevenueAll data={transactionData} billStatus={billStatus} exportData={transactionDataExport} calculateAdvance={calculateAdvance} crm={crm} />
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
    </Box >
  )
}
